import { FC, Fragment, ReactNode, useEffect } from 'react';
import {
  Box,
  List,
  Pagination,
} from '@mui/material';

import { usePagination } from '@cvt/hooks/usePagination';
import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useUniversities } from '../hooks/useUniversities';

const displayAmount = 10;

interface ViewProps {
  filters?: Universities.GetListParams;
  renderItem: (university: Universities.University) => ReactNode;
  options?: {
    enablePagination?: boolean;
  };
  slots?: {
    after?: ReactNode | ((props: { count: number, universities: Universities.University[] }) => ReactNode);
  };
}

export const UniversitiesList: FC<ViewProps> = ({ filters = {}, renderItem, options, slots }) => {

  const { totalPages, setCount, page, setPage, limit, offset } = usePagination(filters.limit || displayAmount);

  const { count, universities, status } = useUniversities({
    ...filters,
    offset,
    limit,
  });

  useEffect(() => {
    if (status === 'success') {
      setCount(count);
    }
  }, [status, setCount, count]);

  const renderAfterSlot = () => {
    if (!slots?.after) return null;
    
    if (typeof slots.after === 'function') {
      return slots.after({
        count,
        universities,
      });
    }
    
    return slots.after;
  };

  return (
    <Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%"/>
      )}
      {status === 'success' && (
        <Fragment>
          {universities.length === 0 && (
            <MessageFeedbackView height={400} message="No Universities"/>
          )}
          {universities.length > 0 && (
            <List disablePadding>
              {universities.map(university => renderItem(university))}
            </List>
          )}
          {renderAfterSlot()}
          {options?.enablePagination && universities.length > 0 && (
            <Box display="flex" width="100%" p={2} mt="auto" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                showFirstButton={page !== 1}
                showLastButton={page !== totalPages}
              />
            </Box>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
